import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Card, Input, Button, Avatar, Typography, Space, Tag, Spin, message,
  Popconfirm, Empty, Tooltip, Alert,
} from 'antd';
import {
  SendOutlined, ArrowLeftOutlined, RobotOutlined, UserOutlined,
  ClearOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { aiSalesAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const QUICK_ACTIONS = [
  { label: '幫我發一篇 FB 推廣文', message: '請現在就用 [TRIGGER_FB_POST] 幫我發一篇 Facebook 推廣文。' },
  { label: '寫一篇本月趨勢 blog', message: '請現在就用 [TRIGGER_BLOG topic="本月行銷趨勢觀察"] 幫我產一篇 blog。' },
  { label: '群發 Email 給客戶', message: '請現在就用 [TRIGGER_EMAIL_BLAST] 群發一封產品推廣 email。' },
  { label: '我的業績如何？', message: '請依 SPO 系統的最新業績數字告訴我目前累計營收、本月業績、最近 5 筆活動。' },
];

export default function BotChatPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bot, setBot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const bottomRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [botRes, histRes] = await Promise.all([
        aiSalesAPI.getBot(id),
        aiSalesAPI.getBotChatHistory(id, 200),
      ]);
      setBot(botRes.data ?? botRes);
      setMessages((histRes.data ?? histRes).messages || []);
    } catch (e) {
      message.error(`載入失敗：${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setSending(true);
    setInput('');
    const localId = `local-${Date.now()}`;
    setMessages(prev => [...prev, { id: localId, role: 'user', content: msg, created_at: new Date().toISOString() }]);
    try {
      const res = await aiSalesAPI.chatWithBot(id, msg);
      const body = res.data ?? res;
      setMessages(prev => [
        ...prev.filter(m => m.id !== localId),
        { id: `local-u-${Date.now()}`, role: 'user', content: msg, created_at: new Date().toISOString() },
        {
          id: `local-a-${Date.now() + 1}`,
          role: 'assistant',
          content: body.reply,
          tokens_used: body.tokens,
          triggered_actions: body.triggered_actions,
          mode: body.mode,
          agent: body.agent,
          created_at: new Date().toISOString(),
        },
      ]);
      if (body.triggered_actions?.length) {
        message.success(`已觸發 ${body.triggered_actions.length} 個背景動作（背景執行中）`);
      }
    } catch (e) {
      message.error(`對話失敗：${e.message}`);
      setMessages(prev => prev.filter(m => m.id !== localId));
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    try {
      await aiSalesAPI.clearBotChatHistory(id);
      setMessages([]);
      message.success('已清空對話');
    } catch (e) {
      message.error(`清空失敗：${e.message}`);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!bot) return <Empty description="找不到 bot" />;

  return (
    <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/ai-sales')}>返回</Button>
          <Avatar size={40} icon={<RobotOutlined />} style={{ backgroundColor: '#3b82f6' }} />
          <div>
            <div style={{ fontWeight: 600 }}>{bot.name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {bot.country_code && `${bot.country_code} · `}
              {bot.alayaess_bot_name ? `綁定 AlayaEss: ${bot.alayaess_bot_name}` : '未綁定 AlayaEss（使用 Mika fallback）'}
            </Text>
          </div>
        </Space>
        <Popconfirm title="清空整段對話歷史？" onConfirm={handleClear} okText="清空" cancelText="取消">
          <Button danger size="small" icon={<ClearOutlined />}>清空對話</Button>
        </Popconfirm>
      </div>

      {!bot.alayaess_bot_name && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 8 }}
          message="此 bot 尚未綁定 AlayaEss agent"
          description={<>對話會 fallback 到 Mika 的人設。請先在 <Link to="/alayaess-bindings">綁定管理</Link> 把它連到一個 AlayaEss agent。</>}
        />
      )}

      {/* Chat scroll area */}
      <Card bodyStyle={{ padding: 12 }} style={{ flex: 1, overflowY: 'auto', marginBottom: 8 }}>
        {messages.length === 0 ? (
          <Empty description="開始對話 — 試試下面的 quick actions">
            <Text type="secondary" style={{ fontSize: 12 }}>
              你可以問業績、請他發文、寫 blog、群發 email；他會用真實 SPO 數字回答。
            </Text>
          </Empty>
        ) : (
          messages.map(m => (
            <div key={m.id} style={{
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 12,
            }}>
              {m.role === 'assistant' && (
                <Avatar size={36} icon={<RobotOutlined />} style={{ backgroundColor: '#3b82f6', marginRight: 8 }} />
              )}
              <div style={{
                maxWidth: '70%',
                padding: '8px 12px',
                borderRadius: 10,
                background: m.role === 'user' ? '#dbeafe' : '#f3f4f6',
                color: '#1f2937',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                <div>{m.content}</div>
                <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>
                  {m.created_at && new Date(m.created_at).toLocaleTimeString('zh-TW')}
                  {m.tokens_used ? ` · ${m.tokens_used} tok` : ''}
                  {m.mode && ` · Mode ${m.mode}`}
                  {m.agent && ` · ${m.agent}`}
                </div>
                {m.triggered_actions?.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    {m.triggered_actions.map((t, i) => (
                      <Tag key={i} color="orange" icon={<ThunderboltOutlined />} style={{ marginBottom: 2 }}>
                        已觸發：{t.action}{t.params?.topic ? ` (topic=${t.params.topic})` : ''}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
              {m.role === 'user' && (
                <Avatar size={36} icon={<UserOutlined />} style={{ marginLeft: 8 }} />
              )}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </Card>

      {/* Quick actions */}
      <div style={{ marginBottom: 8 }}>
        <Space wrap size={[6, 6]}>
          {QUICK_ACTIONS.map((q, i) => (
            <Tooltip key={i} title={q.message}>
              <Button size="small" onClick={() => send(q.message)} disabled={sending}>
                {q.label}
              </Button>
            </Tooltip>
          ))}
        </Space>
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8 }}>
        <TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="跟 bot 說話（Enter 送出，Shift+Enter 換行）"
          autoSize={{ minRows: 1, maxRows: 4 }}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={sending}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={() => send()}
          loading={sending}
          disabled={!input.trim()}
        >送出</Button>
      </div>
    </div>
  );
}
